import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import { Cancel } from "@mui/icons-material";
import { getTranslation, updateTranslation } from "../../store/Language/action";
import { Toast } from "../../util/Toast";

const TranslationInfoDialog = ({ open, handleClose, language }) => {
  const dispatch = useDispatch();
  const storeTranslations = useSelector((state) => state.language.translations);

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("app");
  const [localTranslations, setLocalTranslations] = useState({});

  // Reset tab & fetch data when dialogue opens or language/tab changes
  useEffect(() => {
    if (open && language) {
      const code = language.languageCode || language.code;
      dispatch(getTranslation(code, activeTab));
    }
  }, [open, language, activeTab, dispatch]);

  // Sync Redux translations to local state for editing
  useEffect(() => {
    if (storeTranslations && storeTranslations.translations) {
      setLocalTranslations(storeTranslations.translations);
    } else {
      setLocalTranslations({});
    }
  }, [storeTranslations]);

  // Reset search on tab/language change
  useEffect(() => {
    setSearch("");
  }, [activeTab, language]);

  const handleChange = (key, value) => {
    setLocalTranslations((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleUpdate = () => {
    if (!language) return;

    // 🔥 NO CHANGE DETECT
    const storeData = storeTranslations?.translations || {};
    if (JSON.stringify(localTranslations) === JSON.stringify(storeData)) {
      Toast("info", "No changes detected!");
      handleClose();
      return;
    }

    const code = language.languageCode || language.code;
    const payload = {
      languageCode: code,
      module: activeTab,
      translations: localTranslations,
    };
    dispatch(updateTranslation(payload));
    handleClose(); // Close dialog after update
  };

  const filteredKeys = Object.keys(localTranslations).filter(
    (key) =>
      key.toLowerCase().includes(search.toLowerCase()) ||
      String(localTranslations[key]).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <span className="text-danger h5">
          Translations: {language?.languageTitle || language?.name || language?.title || "N/A"}{" "}
          ({language?.languageCode || language?.code || ""})
        </span>
        <IconButton
          style={{ position: "absolute", right: 10, top: 10 }}
          onClick={handleClose}
        >
          <Tooltip title="Close">
            <Cancel className="text-danger" />
          </Tooltip>
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          variant="fullWidth"
          TabIndicatorProps={{ style: { backgroundColor: "#fff" } }}
          textColor="inherit"
          sx={{
            "& .MuiTab-root": { color: "#fff" },
            "& .Mui-selected": { color: "#fff" },
          }}
        >
          <Tab label="App" value="app" />
          <Tab label="Web" value="web" />
        </Tabs>

        {/* Search */}
        <div className="input-group my-3">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search translations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Table */}
        <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
          <table className="table table-striped mb-0">
            <thead>
              <tr>
                <th style={{ width: "35%" }}>Key</th>
                <th style={{ width: "65%" }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {filteredKeys.length === 0 ? (
                <tr>
                  <td colSpan="2" className="text-center py-3 text-secondary">
                    No Data Found
                  </td>
                </tr>
              ) : (
                filteredKeys.map((key) => (
                  <tr key={key}>
                    <td>
                      <span className="badge bg-light text-dark">{key}</span>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={localTranslations[key] || ""}
                        onChange={(e) => handleChange(key, e.target.value)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer Buttons */}
        <div className="mt-4 d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-outline-info"
            onClick={handleClose}
          >
            Close
          </button>
          <button type="button" className="btn btn-danger" onClick={handleUpdate}>
            Submit
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TranslationInfoDialog;